// BLITZ SPRINT — the whole idle arc, compressed into ninety seconds.
//
// A self-contained economy with its own credits, its own five units and its own
// exponential wall. The point is the shape of an idle game felt at speed: the
// first purchase that matters, the tier that suddenly pays for itself, the
// moment the curve gets away from you. Nothing here touches the main save until
// the timer runs out.
import * as Fx from '../core/Fx.js';
import { modeShell, readoutMarkup, resultCard, formatClock } from './chrome.js';

const RUN_MS = 90_000;
// Gentler than the main game's 1.15 on purpose: ninety seconds only feels
// exponential if a MAX buy can actually stack a dozen deep.
const GROWTH = 1.12;
const START_CREDITS = 120;
// Tapping bootstraps the opening — without it the first twenty seconds are dead
// air waiting on 2/s, the opposite of what a sprint should feel like. The gain
// grows with the square root of output, so it is most of your income in the
// first ten seconds and rounding error by the last ten. That keeps the mode a
// sprint about buying decisions rather than a test of click rate.
const PUSH_SCALE = 3;
const PUSH_FLOOR = 5;

const BLITZ_UNITS = [
  { tier: 1, name: 'Duty Section', cost: 5, output: 2 },
  { tier: 2, name: 'Watch Platoon', cost: 60, output: 22 },
  { tier: 3, name: 'Response Company', cost: 700, output: 260 },
  { tier: 4, name: 'Task Group', cost: 8_000, output: 3_000 },
  { tier: 5, name: 'Theatre Command', cost: 95_000, output: 36_000 },
];

// Milestones give the run its beats — each one is a visible, celebrated step.
const MILESTONES = [
  { at: 5e2, name: 'SECTION STRENGTH' },
  { at: 5e3, name: 'PLATOON STRENGTH' },
  { at: 5e4, name: 'COMPANY STRENGTH' },
  { at: 5e5, name: 'BATTALION STRENGTH' },
  { at: 5e6, name: 'THEATRE STRENGTH' },
  { at: 5e7, name: 'STRATEGIC CEILING' },
];

function costOf(unit, owned, amount = 1) {
  // Geometric series: unit.cost * GROWTH^owned * (GROWTH^amount - 1)/(GROWTH-1)
  return unit.cost * (GROWTH ** owned) * ((GROWTH ** amount) - 1) / (GROWTH - 1);
}

function short(value) {
  if (!Number.isFinite(value)) return '∞';
  if (value < 1000) return value.toFixed(value < 10 ? 1 : 0);
  const units = ['K', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx'];
  let index = -1;
  let scaled = value;
  while (scaled >= 1000 && index < units.length - 1) { scaled /= 1000; index += 1; }
  return `${scaled.toFixed(scaled < 10 ? 2 : 1)}${units[index]}`;
}

export const blitz = {
  id: 'blitz',
  name: 'Blitz Sprint',
  kicker: 'ECONOMY / 90 SEC',
  tagline: 'One idle run, start to exponential, in ninety seconds.',
  description: 'A miniature economy that starts from nothing and gets away from you fast. Buy, compound, and see how far the curve carries before the clock stops.',
  create(ctx) {
    let dom = null;
    let raf = 0;
    let run = null;
    let alive = true;

    function newRun() {
      return {
        credits: START_CREDITS,
        earned: 0,
        pushes: 0,
        owned: { 1: 1, 2: 0, 3: 0, 4: 0, 5: 0 },
        milestone: -1,
        endsAt: performance.now() + RUN_MS,
        lastFrame: performance.now(),
        over: false,
      };
    }

    function rate() {
      return BLITZ_UNITS.reduce((total, unit) => total + run.owned[unit.tier] * unit.output, 0);
    }

    function buy(tier, amount) {
      const unit = BLITZ_UNITS.find((entry) => entry.tier === tier);
      const owned = run.owned[tier];
      const count = amount === 'max' ? maxAffordable(unit, owned) : amount;
      if (count < 1) return;
      const price = costOf(unit, owned, count);
      if (price > run.credits) return;
      run.credits -= price;
      run.owned[tier] += count;
      const button = dom.body.querySelector(`[data-blitz-buy="${tier}"][data-blitz-count="${amount}"]`);
      const point = button ? button.getBoundingClientRect() : null;
      if (point) {
        Fx.floatText(point.left + point.width / 2, point.top, `+${count} ${unit.name}`, { tone: 'cyan' });
        Fx.burst(point.left + point.width / 2, point.top + point.height / 2, { count: 8, tone: 'cyan', spread: 42 });
      }
      paint();
    }

    function push(clientX, clientY) {
      const gain = Math.max(PUSH_FLOOR, Math.sqrt(rate()) * PUSH_SCALE);
      run.credits += gain;
      run.earned += gain;
      run.pushes += 1;
      Fx.floatText(clientX, clientY - 14, `+${short(gain)}`, { tone: 'amber', drift: (Math.random() - 0.5) * 44 });
      Fx.burst(clientX, clientY, { count: 7, tone: 'amber', spread: 46 });
      checkMilestone();
      paint();
    }

    function maxAffordable(unit, owned) {
      const base = unit.cost * (GROWTH ** owned);
      const ratio = (run.credits * (GROWTH - 1)) / base + 1;
      if (ratio <= 1) return 0;
      return Math.max(0, Math.floor(Math.log(ratio) / Math.log(GROWTH)));
    }

    function checkMilestone() {
      const next = MILESTONES[run.milestone + 1];
      if (!next || run.earned < next.at) return;
      run.milestone += 1;
      Fx.banner(next.name, `${short(run.earned)} EARNED`, 'amber');
      Fx.flash('amber', 0.22);
      Fx.shake(4);
    }

    function finish() {
      if (run.over) return;
      run.over = true;
      cancelAnimationFrame(raf);
      const score = run.earned;
      const best = ctx.recordBest(Math.round(score));
      // Payout tracks the order of magnitude reached, not the raw number, so a
      // strong sprint is worth real time without ever exploding.
      const magnitude = Math.max(0, Math.log10(Math.max(1, score)));
      const seconds = Math.min(900, Math.round(magnitude ** 2.1 * 2.4));
      const glimmer = Math.min(40, Math.max(1, run.milestone + 1 + Math.floor(magnitude / 2)));
      ctx.commit({ seconds, glimmer, label: `Blitz Sprint · ${short(score)} earned` });
      Fx.flash('cyan', 0.3);
      resultCard(dom.body, {
        title: `${short(score)} earned in ninety seconds.`,
        lines: [
          ['Peak output', `${short(rate())}/s`],
          ['Milestones cleared', `${run.milestone + 1} / ${MILESTONES.length}`],
          ['Tempo pushes', run.pushes],
          ['Formation', BLITZ_UNITS.map((unit) => run.owned[unit.tier]).join(' · ')],
        ],
        seconds,
        glimmer,
        best: short(best),
      });
      dom.body.querySelector('[data-role="again"]')?.addEventListener('click', startRun);
      dom.body.querySelector('[data-role="done"]')?.addEventListener('click', () => ctx.exit());
    }

    function paint() {
      const output = rate();
      const shopRows = BLITZ_UNITS.map((unit) => {
        const owned = run.owned[unit.tier];
        const one = costOf(unit, owned, 1);
        const maxCount = maxAffordable(unit, owned);
        const share = output > 0 ? (owned * unit.output) / output : 0;
        return `<article class="blitz-row ${run.credits >= one ? 'blitz-ready' : ''}">
          <div class="blitz-tier">${unit.tier}</div>
          <div class="blitz-info">
            <strong>${unit.name}</strong>
            <small>${short(unit.output)}/s each · owned ${owned}</small>
            <div class="blitz-share"><span style="width:${(share * 100).toFixed(1)}%"></span></div>
          </div>
          <button class="blitz-buy" data-blitz-buy="${unit.tier}" data-blitz-count="1" ${run.credits >= one ? '' : 'disabled'}>
            <span>BUY 1</span><b>${short(one)}</b>
          </button>
          <button class="blitz-buy blitz-max" data-blitz-buy="${unit.tier}" data-blitz-count="max" ${maxCount >= 1 ? '' : 'disabled'}>
            <span>MAX</span><b>×${maxCount}</b>
          </button>
        </article>`;
      }).join('');
      const shop = dom.body.querySelector('[data-role="shop"]');
      if (shop) shop.innerHTML = shopRows;
      const bank = dom.body.querySelector('[data-role="bank"]');
      if (bank) bank.innerHTML = `<small>CREDITS</small><b>${short(run.credits)}</b><em>${short(output)}/s</em>`;
    }

    function loop(now) {
      if (!alive || run.over) return;
      const delta = Math.min(0.25, (now - run.lastFrame) / 1000);
      run.lastFrame = now;
      const gain = rate() * delta;
      run.credits += gain;
      run.earned += gain;
      checkMilestone();
      const remaining = run.endsAt - now;
      dom.readout.innerHTML = readoutMarkup([
        ['TIME', formatClock(remaining), remaining < 15000 ? 'stat-danger' : ''],
        ['EARNED', short(run.earned)],
        ['OUTPUT', `${short(rate())}/s`],
        ['MILESTONE', run.milestone >= 0 ? MILESTONES[run.milestone].name : '—'],
      ]);
      const meter = dom.body.querySelector('[data-role="timer"]');
      if (meter) meter.style.width = `${Math.max(0, (remaining / RUN_MS) * 100).toFixed(2)}%`;
      paint();
      if (remaining <= 0) { finish(); return; }
      raf = requestAnimationFrame(loop);
    }

    function onClick(event) {
      if (run.over) return;
      if (event.target.closest('[data-role="push"]')) { push(event.clientX, event.clientY); return; }
      const button = event.target.closest('[data-blitz-buy]');
      if (!button) return;
      const amount = button.dataset.blitzCount === 'max' ? 'max' : Number(button.dataset.blitzCount);
      buy(Number(button.dataset.blitzBuy), amount);
    }

    function startRun() {
      run = newRun();
      dom.body.innerHTML = `<div class="blitz-stage">
        <div class="blitz-timer"><span data-role="timer"></span></div>
        <div class="blitz-bank" data-role="bank"></div>
        <button class="blitz-push" data-role="push"><b>PUSH THE TEMPO</b><small>KICKSTARTS THE CURVE · FADES AS OUTPUT GROWS</small></button>
        <div class="blitz-shop" data-role="shop"></div>
        <p class="blitz-hint">NINETY SECONDS. EVERY PURCHASE COMPOUNDS — SPEND EARLY, SPEND OFTEN.</p>
      </div>`;
      dom.body.addEventListener('click', onClick);
      paint();
      cancelAnimationFrame(raf);
      run.lastFrame = performance.now();
      raf = requestAnimationFrame(loop);
    }

    return {
      mount(root) {
        dom = modeShell(root, blitz);
        dom.exit.addEventListener('click', () => ctx.exit());
        startRun();
      },
      destroy() {
        alive = false;
        cancelAnimationFrame(raf);
        dom?.body.removeEventListener('click', onClick);
      },
    };
  },
};
